import React, { useState } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { Steps } from "./step-library"
import { Container, FlexContainer, H2 } from "@carrotfertility/carotene"
import { Skip } from "../../../views/fle/Molecules"
import ButtonSelectGroup from "../../shared/ButtonSelectGroup"
import useButtonSingleSelect from "../../hooks/useButtonSingleSelect"
import { Label } from "components/views/atoms/Atoms"
import { FormInput } from "components/views/atoms/forms/FormInput"
import { ValidationMessage } from "components/views/atoms/forms/ValidationMessage"
import { useRoutingInfo } from "components/carrot-plans/context/RoutingInfoContext"
import ArrowSubmitButton from "components/carrot-plans/shared/ArrowSubmitButton"
import { useJourneyFlow } from "../../shared/JourneyFlowContainer"

export function GestationalCarrierInMindStep(): JSX.Element {
  const { updateRoutingInfo, routingData } = useRoutingInfo()
  const { handleContinueLoading, continueLoading } = useJourneyFlow()
  const defaultValue = routingData?.gestationalCarrierInMind
  const { setNextStep } = useStateMachine(Steps.GESTATIONAL_CARRIER_IN_MIND, defaultValue ? "" : null)
  const { valid, ...buttonProps } = useButtonSingleSelect({
    options: [
      { title: "Yes", id: "YES" },
      { title: "No", id: "NO" }
    ],
    defaultValue,
    onSubmit: onClickContinue
  })
  async function onClickContinue(selectedValue: string): Promise<void> {
    if (selectedValue !== null) {
      await handleContinueLoading(() => updateRoutingInfo({ gestationalCarrierInMind: selectedValue }))
      setNextStep(Steps.GESTATIONAL_CARRIER_IN_MIND, "")
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onClickContinue(defaultValue)
      }}
    >
      <H2 stack="large">Do you already have a gestational carrier in mind?</H2>
      <Container>
        <ButtonSelectGroup {...buttonProps} />
      </Container>
      <FlexContainer paddingTopBottom="huge" justifyContent="flex-end">
        <ArrowSubmitButton enabled={valid}>Continue</ArrowSubmitButton>
      </FlexContainer>
    </form>
  )
}

export function GestationalCarrierDeliveryStep(): JSX.Element {
  const { updateRoutingInfo, routingData, hasQuestionBeenSkipped } = useRoutingInfo()
  const [defaultCountry = null, defaultState = null] = [
    routingData?.gestationalCarrierDeliverLocation?.country,
    routingData?.gestationalCarrierDeliverLocation?.state
  ]
  const [stateInputValue, setStateInputValue] = useState(defaultState)
  const [countryInputValue, setCountryInputValue] = useState(defaultCountry)
  const filledOut = defaultState !== null && defaultCountry !== null
  const { setNextStep } = useStateMachine(
    Steps.GESTATIONAL_CARRIER_DELIVERY,
    filledOut || hasQuestionBeenSkipped("gestationalCarrierDeliverLocation") ? "" : null
  )
  const { handleContinueLoading, continueLoading } = useJourneyFlow()

  function sanitizeInputs(inputValue) {
    return inputValue === null || inputValue.split("").every((char) => char === " ") || inputValue === ""
      ? null
      : inputValue
  }

  async function onSkip(): Promise<void> {
    await handleContinueLoading(() =>
      updateRoutingInfo({
        skipFields: ["gestationalCarrierDeliverLocation"]
      })
    )
    setNextStep(Steps.GESTATIONAL_CARRIER_DELIVERY, "")
  }

  async function onSubmit(): Promise<void> {
    const sanitizedCountryInput = sanitizeInputs(countryInputValue)
    const sanitizedStateInput = sanitizeInputs(stateInputValue)
    await handleContinueLoading(() =>
      updateRoutingInfo({
        gestationalCarrierDeliverLocation: {
          country: sanitizedCountryInput,
          state: sanitizedStateInput
        }
      })
    )
    setNextStep(Steps.GESTATIONAL_CARRIER_DELIVERY, "")
  }
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <H2 stack="large">Where do you hope to have your gestational carrier deliver?</H2>
      <Container>
        <Container stack="tiny">
          <Label valid={true} htmlFor="stateProvince">
            State/province
          </Label>
        </Container>
        <FormInput
          id="stateProvince"
          value={stateInputValue}
          onChange={(event) => setStateInputValue(event.target.value)}
        />
        <ValidationMessage errors={[]} name="" />
      </Container>
      <Container>
        <Container stack="tiny">
          <Label valid={true} htmlFor="country">
            Country
          </Label>
        </Container>
        <FormInput
          id="country"
          value={countryInputValue}
          onChange={(event) => setCountryInputValue(event.target.value)}
        />
        <ValidationMessage errors={[]} name="" />
      </Container>
      <FlexContainer paddingTopBottom="huge" justifyContent="space-between">
        <Skip onClick={onSkip} onClickOnly enabled={!continueLoading} />
        <ArrowSubmitButton enabled={!continueLoading}>Continue</ArrowSubmitButton>
      </FlexContainer>
    </form>
  )
}
